import { Survey, SurveySubmission, AlarmData, SurveyTargetGroup, SurveySection, UserSurvey, ReportOptions, ReportSubmission } from '../types/survey';

// Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Fetch sections from server based on target group
export const fetchSectionsByAge = async (age: number): Promise<SurveySection[]> => {
	const targetGroup = age >= 18 ? 'adults' : 'kids';

	try {
		const url = `${API_BASE_URL}/sections?targetGroup=${targetGroup}`;
		const response = await fetch(url);

		if (!response.ok) {
			throw new Error(`Neuspešno dohvatanje sekcija: ${response.statusText}`);
		}

		return await response.json();
	} catch (error) {
		console.error('Error fetching sections:', error);
		// Return mock sections for development/demo purposes
		return [];
	}
};
export const fetchSectionsForHealth = async (): Promise<SurveySection[]> => {
	try {
		const url = `${API_BASE_URL}/sections?userType=health`;
		const response = await fetch(url);

		if (!response.ok) {
			throw new Error(`Neuspešno dohvatanje sekcija: ${response.statusText}`);
		}

		return await response.json();
	} catch (error) {
		console.error('Error fetching sections:', error);
		// Return mock sections for development/demo purposes
		return [];
	}
};

// Legacy function for compatibility - converts sections to survey format
export const fetchSurveyByAge = async (age: number): Promise<Survey> => {
	const sections = await fetchSectionsByAge(age);
	const targetGroup = age >= 18 ? 'adults' : 'kids';

	return {
		id: `survey-${targetGroup}`,
		title: 'Procena rizika od trgovine ljudima',
		description: 'Ovaj upitnik pomaže u identifikaciji potencijalnih situacija trgovine ljudima.',
		targetGroup,
		sections,

		questions: sections.flatMap((section) =>
			section.questions.map((question) => ({
				...question,
				text: question.title, // Map title to text for compatibility
				type: 'boolean' as const,
				required: true,
				answer: null,
				riskWeight: question.score,
				category: section.id,
			}))
		),
		alarmThreshold: 25,
	};
};

// Submit survey responses to new API
export const submitSurvey = async (submission: SurveySubmission): Promise<UserSurvey> => {
	try {
		const response = await fetch(`${API_BASE_URL}/user-surveys`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(submission),
		});

		if (!response.ok) {
			throw new Error(`Neuspešno slanje ankete: ${response.statusText}`);
		}

		return await response.json();
	} catch (error) {
		console.error('Error submitting survey:', error);
		throw error;
	}
};

// Send alarm to API endpoint (legacy function)
export const sendAlarm = async (alarmData: AlarmData): Promise<void> => {
	try {
		const response = await fetch(`${API_BASE_URL}/alarms`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(alarmData),
		});

		if (!response.ok) {
			throw new Error(`Neuspešno slanje alarma: ${response.statusText}`);
		}
	} catch (error) {
		console.error('Error sending alarm:', error);
		// In production, you might want to implement retry logic
	}
};

// Get report options from server
export const getReportOptions = async (): Promise<ReportOptions> => {
	return {
		countries: [
			'Afganistan',
			'Albanija',
			'Alžir',
			'Andora',
			'Angola',
			'Antigua i Barbuda',
			'Argentina',
			'Jermenija',
			'Australija',
			'Austrija',
			'Azerbejdžan',
			'Bahami',
			'Bahrein',
			'Bangladeš',
			'Barbados',
			'Belorusija',
			'Belgija',
			'Beliz',
			'Benin',
			'Butan',
			'Bolivija',
			'Bosna i Hercegovina',
			'Bocvana',
			'Brazil',
			'Brunej',
			'Bugarska',
			'Burkina Faso',
			'Burundi',
			'Kambodža',
			'Kamerun',
			'Kanada',
			'Zelenortska Ostrva',
			'Centralnoafrička Republika',
			'Čad',
			'Čile',
			'Kina',
			'Kolumbija',
			'Komori',
			'Kongo',
			'Demokratska Republika Kongo',
			'Kostarika',
			'Obala Slonovače',
			'Hrvatska',
			'Kuba',
			'Kipar',
			'Češka',
			'Danska',
			'Džibuti',
			'Dominika',
			'Dominikanska Republika',
			'Ekvador',
			'Egipat',
			'El Salvador',
			'Ekvatorijalna Gvineja',
			'Eritreja',
			'Estonija',
			'Etiopija',
			'Fidži',
			'Finska',
			'Francuska',
			'Gabon',
			'Gambija',
			'Gruzija',
			'Nemačka',
			'Gana',
			'Grčka',
			'Grenada',
			'Gvatemala',
			'Gvineja',
			'Gvineja-Bisao',
			'Gvajana',
			'Haiti',
			'Honduras',
			'Mađarska',
			'Island',
			'Indija',
			'Indonezija',
			'Iran',
			'Irak',
			'Irska',
			'Izrael',
			'Italija',
			'Jamajka',
			'Japan',
			'Jordan',
			'Kazahstan',
			'Kenija',
			'Kiribati',
			'Severna Koreja',
			'Južna Koreja',
			'Kuvajt',
			'Kirgistan',
			'Laos',
			'Letonija',
			'Liban',
			'Lesoto',
			'Liberija',
			'Libija',
			'Lihtenštajn',
			'Litvanija',
			'Luksemburg',
			'Madagaskar',
			'Malavi',
			'Malezija',
			'Maldivi',
			'Mali',
			'Malta',
			'Maršalova Ostrva',
			'Mauritanija',
			'Mauricijus',
			'Meksiko',
			'Mikronezija',
			'Moldavija',
			'Monako',
			'Mongolija',
			'Crna Gora',
			'Maroko',
			'Mozambik',
			'Mijanmar',
			'Namibija',
			'Nauru',
			'Nepal',
			'Holandija',
			'Novi Zeland',
			'Nikaragva',
			'Niger',
			'Nigerija',
			'Severna Makedonija',
			'Norveška',
			'Oman',
			'Pakistan',
			'Palau',
			'Panama',
			'Papua Nova Gvineja',
			'Paragvaj',
			'Peru',
			'Filipini',
			'Poljska',
			'Portugalija',
			'Katar',
			'Rumunija',
			'Rusija',
			'Ruanda',
			'Sveti Kits i Nevis',
			'Sveta Lucija',
			'Sveti Vinsent i Grenadini',
			'Samoa',
			'San Marino',
			'Sao Tome i Principe',
			'Saudijska Arabija',
			'Senegal',
			'Srbija',
			'Sejšeli',
			'Sijera Leone',
			'Singapur',
			'Slovačka',
			'Slovenija',
			'Solomonova Ostrva',
			'Somalija',
			'Južnoafrička Republika',
			'Južni Sudan',
			'Španija',
			'Šri Lanka',
			'Sudan',
			'Surinam',
			'Švedska',
			'Švajcarska',
			'Sirija',
			'Tajvan',
			'Tadžikistan',
			'Tanzanija',
			'Tajland',
			'Timor-Leste',
			'Togo',
			'Tonga',
			'Trinidad i Tobago',
			'Tunis',
			'Turska',
			'Turkmenistan',
			'Tuvalu',
			'Uganda',
			'Ukrajina',
			'Ujedinjeni Arapski Emirati',
			'Ujedinjeno Kraljevstvo',
			'Sjedinjene Američke Države',
			'Urugvaj',
			'Uzbekistan',
			'Vanuatu',
			'Vatikan',
			'Venecuela',
			'Vijetnam',
			'Jemen',
			'Zambija',
			'Zimbabve',
		],
		socialStatuses: [
			{ id: '1', name: 'Nezaposlen/a', value: 'unemployed' },
			{ id: '2', name: 'Student/kinja', value: 'student' },
			{ id: '3', name: 'Privremeno zaposlen/a', value: 'temporary_employment' },
			{ id: '4', name: 'Stalno zaposlen/a', value: 'permanent_employment' },
			{ id: '5', name: 'Penzioner/ka', value: 'retired' },
			{ id: '6', name: 'Nepoznato', value: 'unknown' },
		],
		healthStatuses: ['Mentalna oboljenja', 'Lake telesne povrede', 'Teške telesne povrede', 'Povrede unutrašnjih organa', 'Hronična oboljenja', 'Invaliditet', 'Osoba ne može da se kreće', 'Osoba ne može sama da se brine o sebi', 'Osobi je potrebno hitno medicinsko zbrinjavanje'],
		exploitationTypes: ['Seksualna eksploatacija', 'Prinudni brak u svrhu eksploatacije', 'Prinuda na prosjačenje', 'Prinuda na vršenje krivičnih dela', 'Radna eksploatacija', 'Prinuda na učešće u oružanim sukobima', 'Oduzimanje organa', 'Trgovina ljudima u svrhu ilegalnog usvojenja'],
		maritalStatus: ['Neoženjen/neudata', 'U braku', 'Vanbračna zajednica', 'Udovac/ca', 'Razveden/a', 'Prekinuta vanbračna zajednica'],
		placesWhereLives: ['U kući / stanu u kome živi', 'Kod rođaka / prijatelja', 'Kod nepoznatih ljudi', 'U migrantskom centru', 'U improvizovanom kampu', 'Na ulici', 'Kod potencijalnih trafiketa', 'U bolnici', 'U centru za socijalni rad', 'Drugo'],
		specialStatus: ['Bez posebnog statusa', 'Interno raseljeno lice', 'Izbeglo lice', 'Lice iz readmisije', 'Tražilac azila', 'Migrant/izbeglica', 'Osoba koja je dobila azil'],
		exploitationPhases: ['Faza vrbovanja', 'Faza eksploatacije u toku', 'Eksploatacija je završena'],
		exploitationPlaces: ['U kući / stanu u kome živi', 'Kod rođaka / prijatelja', 'Kod nepoznatih ljudi', 'U migrantskom centru', 'U improvizovanom kampu', 'Na ulici', 'Kod potencijalnih trafiketa', 'U bolnici', 'U centru za socijalni rad', 'Drugo'],
		stayingWithTypes: ['Sam/sama', 'Sa nepoznatim osobama', 'Sa supružnikom', 'Sa decom', 'Sa prijateljima', 'Sa srodnicima', 'Sa starateljima '],
		genders: ['Muški', 'Ženski', 'Transrodna osoba', 'Gender fluidna osoba', 'Istalo', 'Nepoznato'],
		supportTypes: [
			'Medicinska pomoć',
			'Hospitalizacija',
			'Pomoć u nabavci lekova i medicinskih sredstava',
			'Smeštaj',
			'Informisanje o pravima',
			'Psihološka podrška i savetovanje',
			'Pravna pomoć',
			'Povratak u zemlju porekla',
			'Dozvola boravka',
			'Osiguranje bezbednosti (bezbednost pretpostavljene žrtve je ugrožena)',
			'Uspostavljanje kontakta sa porodicom',
			'Starateljska zaštita ',
			'Materijalna pomoć',
			'Pomoć u obezbeđivanju hrane',
			'Prevoz ',
			'Regulisanje ličnih dokumenata',
			'Ostalo ',
		],
	};
};

// Submit report to server
export const submitReport = async (reportData: ReportSubmission, token: string): Promise<void> => {
	try {
		const response = await fetch(`${API_BASE_URL}/reports`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(reportData),
		});

		if (!response.ok) {
			throw new Error(`Neuspešno slanje izveštaja: ${response.statusText}`);
		}
	} catch (error) {
		console.error('Error submitting report:', error);
		throw error;
	}
};
